import React from 'react';
import styled from '@emotion/styled';
import { IPrice } from '../../../../../shared/i18n/price/IPrice';
import { getDifference } from '../../../../../shared/i18n/price/utils/getDifference';
import { getFormattedPrice } from '../../../../../shared/i18n/price/utils/getFormattedPrice';
import { useTranslation } from '../../../../../shared/i18n/translate/useTranslation';
import { BadgeTag } from '../../../../../account/ui/shared/BadgeTag';
import { useAuthStore } from '../../../../../auth/shared/store/useAuthStore';
import { useFormatNumberWithSeparator } from '../../../../../shared/utils/useFormatNumberWithSeparator';
import { Color } from '../../../../../shared/styles/Color';
import { VisuallyHidden } from '../../../../../../ui';

interface IParkingProductPriceProps {
	price: IPrice;
	originalPrice: IPrice;
	className?: string;
	loyaltyPoints?: number;
}

export const PriceWrap = styled.div<{ align?: 'right' }>`
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	align-items: ${({ align }) => (!align ? 'flex-start' : 'flex-end')};
`;

export const OriginalPrice = styled.div`
	position: relative;
	font-size: 20px;
	margin-right: 10px;
	line-height: 1em;

	&:after {
		content: '';
		position: absolute;
		left: 0;
		top: 50%;
		left: -1px;
		right: -2px;
		height: 2px;
		margin-top: -1px;
		background: ${Color.ErrorDark};
	}
`;

export const Price = styled.div`
	font-weight: 700;
	font-size: 32px;
	line-height: 1em;
`;

export const Savings = styled.div`
	margin-top: 6px;
	color: ${Color.ErrorDark};
`;

const StyledPriceContainer = styled.div`
	display: flex;
	justify-content: space-between;
`;

export const ParkingProductPrice = ({
	price,
	originalPrice,
	className,
	loyaltyPoints,
}: IParkingProductPriceProps) => {
	const { t } = useTranslation();
	const savings = getDifference(originalPrice, price);
	const hasSavings = savings.centAmount > 0;
	const { isAuthenticated } = useAuthStore();
	const { formatWithSeparator } = useFormatNumberWithSeparator();

	return (
		<StyledPriceContainer className={className}>
			<PriceWrap>
				{hasSavings && (
					<OriginalPrice>
						<VisuallyHidden as="span">
							{t('web_parking_availability_results_original_price_aria_label')}
						</VisuallyHidden>

						{getFormattedPrice(originalPrice)}
					</OriginalPrice>
				)}
				<Price>
					<VisuallyHidden as="span">
						{t('web_parking_availability_results_current_price_aria_label')}
					</VisuallyHidden>
					{getFormattedPrice(price)}
				</Price>
			</PriceWrap>

			<PriceWrap align="right">
				{isAuthenticated && !!loyaltyPoints && (
					<BadgeTag
						text={`+${t('loyalty_offer_points_template', {
							points: formatWithSeparator(loyaltyPoints),
						})}`}
					/>
				)}
				{hasSavings && (
					<Savings>
						{t('web_parking_availability_results_price_savings')}
						{': '}
						{getFormattedPrice(savings)}
					</Savings>
				)}
			</PriceWrap>
		</StyledPriceContainer>
	);
};
